import React, { useContext } from 'react'

import { useSetState } from '@domain-group/react-hooks'

import {
  faCamera,
  faDownload,
  faImage,
  faSync,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Box,
  Button,
  FileInput,
  Form,
  Image,
  ModalContext,
  SidebarModal,
  Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useAttachment, { useAttachmentForm } from '@hooks/useAttachment'
import useSelector from '@hooks/useSelector'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import { AttachmentModel, OrderItemModel } from '@models/types'

import { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditProviderAttachmentModal'

const defaultState = { showFileUpload: false }

type ModalCallbacks = {
  closeModal: () => void,
  updateAttachment: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

type ModalPayload = {
  attachment: Partial<AttachmentModel>,
  campaignTitle: string,
  orderItem: OrderItemModel,
}

const CreateOrEditProviderAttachmentModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const {
    closeModal, updateAttachment,
  } = callbacks

  const [state, setState] = useSetState(defaultState)
  const { showFileUpload } = state

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload: ModalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    attachment: initAttachment, campaignTitle, orderItem,
  } = modalPayload

  const {
    attachment,
    callbacks: {
      downloadAttachment,
      updateAttachment: updateFn,
    },
    creating,
    updating,
  } = useAttachment(initAttachment, {
    requestOptions: { include_attachment_file: true },
    performHttpRequests: true,
  })

  const {
    file_file_name: fileName, dimensions, links,
  } = attachment

  const { service_id } = orderItem
  const services = useSelector(reduxState => reduxState.entities.services)

  const service = services[service_id!] || {}
  const imageUrl = links?.thumb_url

  const width = dimensions?.original?.width || 0
  const height = dimensions?.original?.height || 0

  const attachmentFormPayload = useAttachmentForm(attachment)

  const {
    entityState,
    entityState: {
      data: { image_type: imageType },
      file_file_name,
    },
    saveEnabled,
    setEntityState,
  } = attachmentFormPayload

  const updateAttachmentPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.attachment,
    toastText: 'Attachment Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Image" />

      <SidebarModal.SubHeader>
        <Box lineHeight={1.3} flexDirection="column">
          <Text color="bodyFontLightColor" fontSize="small">{campaignTitle}</Text>

          <Text marginTop="large">{orderItem.title}</Text>

          <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="medium">{service.title}</Text>
        </Box>
      </SidebarModal.SubHeader>

      <SidebarModal.Content>
        <Form>
          {showFileUpload && (
            <Form.Field label="* Upload File">
              <FileInput
                input={{
                  onChange: (fileData: string, fileObject: object) => (
                    // @ts-expect-error TS2322
                    updateAttachment({
                      callbacks: {
                        action: updateFn,
                        afterAction: () => setState({ showFileUpload: false }),
                      },
                      // @ts-expect-error TS2339
                      entityParams: { file: fileData, file_file_name: fileObject.name },
                      requestOptions: defaultRequestOptions.attachment,
                    })
                  ),
                  accept: 'image/*',
                }}
              />
            </Form.Field>
          )}

          {!showFileUpload && (
            <>
              <Image
                borderRadius={5}
                height={230}
                url={imageUrl}
              />

              <Form.Field label="Image Type" marginTop="large">
                <Box flexDirection="row" alignItems="center">
                  <Button
                    buttonStyle={imageType === 'photo' ? 'primaryUtility' : 'secondaryUtility'}
                    onClick={() => setEntityState({ data: { image_type: 'photo' } })}
                    // @ts-expect-error TS2322
                    boxProps={{ backgroundColor: imageType === 'photo' ? 'lightestGrey' : '' }}
                    icon={<FontAwesomeIcon icon={faCamera} />}
                    size="medium"
                    style={{ marginRight: 4, width: 'calc(50% - 4px)' }}
                  >
                    Photo
                  </Button>

                  <Button
                    buttonStyle={imageType === 'plan' ? 'primaryUtility' : 'secondaryUtility'}
                    icon={<FontAwesomeIcon icon={faImage} />}
                    onClick={() => setEntityState({ data: { image_type: 'plan' } })}
                    size="medium"
                    style={{
                      marginLeft: 4,
                      textTransform: 'capitalize',
                      width: 'calc(50% - 4px)',
                    }}
                  >
                    Floorplan
                  </Button>
                </Box>
              </Form.Field>

              <Form.Field label="File Name" marginTop="large">
                <Text variant="ellipsis" fontSize="small">
                  {file_file_name || fileName}
                </Text>
              </Form.Field>

              <Form.Field label="Dimensions" marginTop="large">
                <Text variant="ellipsis" fontSize="small">
                  {`${width} x ${height} px`}
                </Text>
              </Form.Field>

              <Form.Field label="More Options" marginTop="large">
                <Button
                  buttonStyle="secondaryUtility"
                  // @ts-expect-error TS2322
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => downloadAttachment(e)}
                  icon={<FontAwesomeIcon icon={faDownload} />}
                  marginTop="medium"
                  size="medium"
                >
                  Download Image
                </Button>

                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faSync} />}
                  marginTop="medium"
                  onClick={() => setState({ showFileUpload: true })}
                  size="medium"
                >
                  Replace Image
                </Button>
              </Form.Field>
            </>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => updateAttachment(updateAttachmentPayload)}
          size="large"
        >
          Update Attachment
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProviderAttachmentModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
